import { ScrollableCurriculumTabStrip } from "@/components/ui/scrollable-curriculum-tab-strip";
import { Tabs, TabsContent, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { BeltRank } from "../../MembersArea";

const DEFAULT_TAB_SURFACE = "!bg-gray-200 !text-gray-900";

interface SoHoYeonProps {
  selectedBelt: BeltRank;
  getBeltColor: (belt: BeltRank) => string;
  hasDemo?: (aliases: string[]) => boolean;
  onWatchDemo?: (aliases: string[]) => void;
  presentation?: "legacy" | "curriculum";
  beltTabSurfaceClass?: string;
}

export default function SoHoYeon({
  selectedBelt,
  getBeltColor: _getBeltColor,
  hasDemo,
  onWatchDemo,
  presentation = "legacy",
  beltTabSurfaceClass = DEFAULT_TAB_SURFACE,
}: SoHoYeonProps) {
  if (!["BLACK/D3", "BLACK/D4", "BLACK/D5"].includes(selectedBelt)) {
    return null;
  }

  const surface = beltTabSurfaceClass;
  const triggerClass = cn(
    "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-3 py-2 text-xs font-medium md:text-sm",
    surface,
    "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
  );

  const demoBtn =
    hasDemo?.(["sohoyen", "so_ho_yen", "sohoyeon", "so_ho_yeon"]) ? (
      <button
        type="button"
        className="mb-3 inline-flex items-center text-sm font-medium text-taekwondo-blue hover:underline"
        onClick={() => onWatchDemo?.(["sohoyen", "so_ho_yen", "sohoyeon", "so_ho_yeon"])}
      >
        Watch Demonstration Video
      </button>
    ) : null;

  const patternInstructions = (
    <div>
      <h4 className="mb-3 text-base font-medium text-taekwondo-red">Pattern Instructions</h4>
      <ol className="list-decimal list-inside space-y-2">
        <li className="text-gray-700">Start in Ready Stance (Joonbi)</li>
        <li className="text-gray-700">Begin with explosive tiger-style movements</li>
        <li className="text-gray-700">Execute quick directional changes with precision</li>
        <li className="text-gray-700">
          Perform dynamic striking combinations <span className="font-semibold">KIHAP</span>
        </li>
        <li className="text-gray-700">Demonstrate agile footwork sequences</li>
        <li className="text-gray-700">Show swift counter techniques</li>
        <li className="text-gray-700">Execute power generation movements</li>
        <li className="text-gray-700">
          Complete final sequence with explosive power <span className="font-semibold">KIHAP</span>
        </li>
        <li className="text-gray-700">Return to Ready Stance (Joonbi)</li>
      </ol>
    </div>
  );

  if (presentation === "curriculum") {
    return (
      <div className="rounded-lg bg-gray-50 p-4 md:p-5">
        <h2 className="mb-2 text-lg font-semibold text-taekwondo-red md:text-xl">SoHoYeon — Little Tiger</h2>
        {demoBtn && <div className="mb-3">{demoBtn}</div>}
        <Tabs defaultValue="pattern-information" className="w-full space-y-4">
          <ScrollableCurriculumTabStrip ariaLabelPrefix="SoHoYeon">
            <TabsTrigger value="pattern-information" className={triggerClass}>
              Pattern Information
            </TabsTrigger>
            <TabsTrigger value="details" className={triggerClass}>
              Details
            </TabsTrigger>
            <TabsTrigger value="key-techniques" className={triggerClass}>
              Key Techniques
            </TabsTrigger>
            <TabsTrigger value="training-focus" className={triggerClass}>
              Training Focus
            </TabsTrigger>
            <TabsTrigger value="pattern" className={triggerClass}>
              Pattern
            </TabsTrigger>
          </ScrollableCurriculumTabStrip>

          <TabsContent value="pattern-information" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-3 text-base font-medium text-taekwondo-red">Pattern Information</h4>
              <p className="mb-4 text-gray-700">
                SoHoYeon, meaning &quot;Little Tiger&quot;, is an advanced pattern that embodies the agility and fierce determination of a young tiger.
                This pattern emphasizes quick, powerful movements combined with precise technical execution.
              </p>
            </div>
          </TabsContent>
          <TabsContent value="details" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-3 text-base font-medium text-taekwondo-red">Pattern Details</h4>
              <ul className="list-disc space-y-1 pl-5 text-gray-700">
                <li>Number of Movements: 54</li>
                <li>Pattern Diagram: Tiger&apos;s Path</li>
                <li>Starting Position: Ready Stance</li>
              </ul>
            </div>
          </TabsContent>
          <TabsContent value="key-techniques" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-3 text-base font-medium text-taekwondo-red">Key Techniques</h4>
              <ul className="list-inside list-disc space-y-1 text-gray-700">
                <li>Quick directional changes</li>
                <li>Explosive striking combinations</li>
                <li>Agile footwork patterns</li>
                <li>Tiger-style hand techniques</li>
                <li>Dynamic power generation</li>
                <li>Swift counter movements</li>
              </ul>
            </div>
          </TabsContent>
          <TabsContent value="training-focus" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-3 text-base font-medium text-taekwondo-red">Training Focus</h4>
              <ul className="list-inside list-disc space-y-1 text-gray-700">
                <li>Speed development</li>
                <li>Explosive power</li>
                <li>Agility training</li>
                <li>Technical precision</li>
                <li>Combat awareness</li>
                <li>Energy management</li>
              </ul>
            </div>
          </TabsContent>
          <TabsContent value="pattern" className="mt-0 focus-visible:outline-none">
            {patternInstructions}
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  return (
    <div className="mt-2 rounded-lg bg-gray-50 p-4">
      <div className="space-y-4">
        <div>
          {demoBtn}
          <h4 className="mb-3 text-base font-medium text-taekwondo-red">Pattern Information</h4>
          <p className="mb-4 text-gray-700">
            SoHoYeon, meaning &quot;Little Tiger&quot;, is an advanced pattern that embodies the agility and fierce determination of a young tiger.
            This pattern emphasizes quick, powerful movements combined with precise technical execution.
          </p>
        </div>

        <div>
          <h4 className="mb-3 text-base font-medium text-taekwondo-red">Pattern Details</h4>
          <ul className="list-disc space-y-1 pl-5 text-gray-700">
            <li>Number of Movements: 54</li>
            <li>Pattern Diagram: Tiger&apos;s Path</li>
            <li>Starting Position: Ready Stance</li>
          </ul>
        </div>

        <div>
          <h4 className="mb-3 text-base font-medium text-taekwondo-red">Key Techniques</h4>
          <ul className="list-inside list-disc space-y-1 text-gray-700">
            <li>Quick directional changes</li>
            <li>Explosive striking combinations</li>
            <li>Agile footwork patterns</li>
            <li>Tiger-style hand techniques</li>
            <li>Dynamic power generation</li>
            <li>Swift counter movements</li>
          </ul>
        </div>

        <div>
          <h4 className="mb-3 text-base font-medium text-taekwondo-red">Training Focus</h4>
          <ul className="list-inside list-disc space-y-1 text-gray-700">
            <li>Speed development</li>
            <li>Explosive power</li>
            <li>Agility training</li>
            <li>Technical precision</li>
            <li>Combat awareness</li>
            <li>Energy management</li>
          </ul>
        </div>

        {patternInstructions}
      </div>
    </div>
  );
}
